import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, Boxes, AlertTriangle, CheckCircle, Clock, RotateCcw, TrendingUp, BarChart3, PieChart as PieChartIcon, RefreshCw, Filter, X, Activity, Scale, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  ComposedChart,
  RadialBarChart,
  RadialBar,
  Treemap
} from "recharts";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StoreDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Raw data
  const [allInventory, setAllInventory] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [lowStockDetails, setLowStockDetails] = useState([]);
  const [consumptionItems, setConsumptionItems] = useState([]);
  const [consumptionHistory, setConsumptionHistory] = useState([]);
  
  // Filter options
  const [productTypes, setProductTypes] = useState([]);
  const [millNames, setMillNames] = useState([]);
  const [dealerNames, setDealerNames] = useState([]);
  
  // Active filters
  const [filters, setFilters] = useState({
    productType: "all",
    millName: "all",
    dealerName: "all",
    status: "all",
    dateFrom: "",
    dateTo: "",
  });
  
  // Filtered data for charts
  const [filteredInventory, setFilteredInventory] = useState([]);
  
  // Stats
  const [stats, setStats] = useState({
    totalMaterials: 0,
    lowStockItems: 0,
    availableStock: 0,
    usedStock: 0,
    returnStock: 0,
    totalValue: 0,
    totalWeight: 0,
    avgReelWeight: 0,
    totalReels: 0,
  });

  // Chart data
  const [productTypeData, setProductTypeData] = useState([]);
  const [millData, setMillData] = useState([]);
  const [weightByProductData, setWeightByProductData] = useState([]);
  const [statusPieData, setStatusPieData] = useState([]);
  const [valueByMillData, setValueByMillData] = useState([]);
  const [stockTrendData, setStockTrendData] = useState([]);
  const [weightDistributionData, setWeightDistributionData] = useState([]);
  const [dealerData, setDealerData] = useState([]);

  const COLORS = ['#10b981', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#84cc16', '#06b6d4', '#f97316'];
  const STATUS_COLORS = { available: '#10b981', stock_used: '#6b7280', used: '#6b7280', return: '#f59e0b' };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allInventory]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [materialsRes, inventoryRes, lowStockRes, consumptionRes, consumptionHistoryRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/materials`),
        axios.get(`${BACKEND_URL}/api/inventory`),
        axios.get(`${BACKEND_URL}/api/materials/low-stock`),
        axios.get(`${BACKEND_URL}/api/consumption-inventory`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/consumption-stock-out/history`).catch(() => ({ data: [] })),
      ]);

      const materials = materialsRes.data;
      const inventory = inventoryRes.data;
      const lowStock = lowStockRes.data;

      setAllMaterials(materials);
      setAllInventory(inventory);
      setLowStockDetails(lowStock.items || []);
      setConsumptionItems(consumptionRes.data);
      setConsumptionHistory(consumptionHistoryRes.data);

      const products = [...new Set(inventory.map(i => i.product).filter(t => t))];
      const mills = [...new Set(inventory.map(i => i.mill_name).filter(t => t))];
      const dealers = [...new Set(inventory.map(i => i.dealer_name).filter(t => t))];
      
      setProductTypes(products);
      setMillNames(mills);
      setDealerNames(dealers);

      setStats(prev => ({
        ...prev,
        totalMaterials: materials.length,
        lowStockItems: lowStock.count,
      }));

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allInventory];

    if (filters.productType !== "all") {
      filtered = filtered.filter(i => i.product === filters.productType);
    }
    if (filters.millName !== "all") {
      filtered = filtered.filter(i => i.mill_name === filters.millName);
    }
    if (filters.dealerName !== "all") {
      filtered = filtered.filter(i => i.dealer_name === filters.dealerName);
    }
    if (filters.status !== "all") {
      filtered = filtered.filter(i => {
        if (filters.status === "stock_used") {
          return i.status === "stock_used" || i.status === "used";
        }
        return i.status === filters.status;
      });
    }
    if (filters.dateFrom) {
      filtered = filtered.filter(i => i.in_date >= filters.dateFrom);
    }
    if (filters.dateTo) {
      filtered = filtered.filter(i => i.in_date <= filters.dateTo);
    }

    setFilteredInventory(filtered);
    updateChartsAndStats(filtered);
  };

  const updateChartsAndStats = (inventory) => {
    const availableStock = inventory.filter(i => i.status === "available").length;
    const usedStock = inventory.filter(i => i.status === "stock_used" || i.status === "used").length;
    const returnStock = inventory.filter(i => i.status === "return").length;
    const availableItems = inventory.filter(i => i.status === "available");
    const totalValue = availableItems.reduce((sum, i) => sum + ((i.paper_price || 0) * (i.reel_weight || 0)), 0);
    const totalWeight = availableItems.reduce((sum, i) => sum + (i.reel_weight || 0), 0);
    const avgReelWeight = availableItems.length > 0 ? totalWeight / availableItems.length : 0;

    setStats(prev => ({
      ...prev,
      availableStock,
      usedStock,
      returnStock,
      totalValue: Math.round(totalValue),
      totalWeight: Math.round(totalWeight),
      avgReelWeight: Math.round(avgReelWeight),
      totalReels: inventory.length,
    }));

    // 1. Product Type Distribution (Donut)
    const productCounts = {};
    inventory.forEach(item => {
      const product = item.product || "Unknown";
      productCounts[product] = (productCounts[product] || 0) + 1;
    });
    setProductTypeData(Object.entries(productCounts).map(([name, value]) => ({ name, value })));

    // 2. Stock by Mill (Horizontal Bar)
    const millCounts = {};
    inventory.filter(i => i.mill_name).forEach(item => {
      millCounts[item.mill_name] = (millCounts[item.mill_name] || 0) + 1;
    });
    setMillData(Object.entries(millCounts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 8));

    // 3. Weight by Product (Stacked Area)
    const weightByProduct = {};
    inventory.forEach(item => {
      const product = item.product || "Unknown";
      weightByProduct[product] = (weightByProduct[product] || 0) + (item.reel_weight || 0);
    });
    setWeightByProductData(Object.entries(weightByProduct).map(([name, weight]) => ({ name, weight: Math.round(weight) })));

    // 4. Status Distribution (Radial)
    setStatusPieData([
      { name: 'Available', value: availableStock, fill: '#10b981' },
      { name: 'Stock Used', value: usedStock, fill: '#6b7280' },
      { name: 'Return', value: returnStock, fill: '#f59e0b' },
    ].filter(d => d.value > 0));

    // 5. Value by Mill (Composed - Bar + Line)
    const valueByMill = {};
    const countByMill = {};
    availableItems.filter(i => i.mill_name).forEach(item => {
      const mill = item.mill_name;
      valueByMill[mill] = (valueByMill[mill] || 0) + ((item.paper_price || 0) * (item.reel_weight || 0));
      countByMill[mill] = (countByMill[mill] || 0) + 1;
    });
    setValueByMillData(Object.entries(valueByMill).map(([name, value]) => ({ 
      name, 
      value: Math.round(value),
      count: countByMill[name] || 0
    })).sort((a, b) => b.value - a.value).slice(0, 6));

    // 6. Stock by Month (Line Chart - simulated trend)
    const monthlyData = {};
    inventory.forEach(item => {
      if (item.in_date) {
        const month = item.in_date.substring(0, 7); // YYYY-MM
        if (!monthlyData[month]) {
          monthlyData[month] = { available: 0, used: 0, return: 0 };
        }
        if (item.status === "available") monthlyData[month].available++;
        else if (item.status === "stock_used" || item.status === "used") monthlyData[month].used++;
        else if (item.status === "return") monthlyData[month].return++;
      }
    });
    const sortedMonths = Object.keys(monthlyData).sort();
    setStockTrendData(sortedMonths.slice(-6).map(month => ({
      month: month.substring(5), // MM only
      ...monthlyData[month]
    })));

    // 7. Weight Distribution (Histogram-like)
    const weightRanges = [
      { range: '0-200', min: 0, max: 200 },
      { range: '201-400', min: 201, max: 400 },
      { range: '401-600', min: 401, max: 600 },
      { range: '601-800', min: 601, max: 800 },
      { range: '800+', min: 801, max: Infinity },
    ];
    const weightDist = weightRanges.map(r => ({
      range: r.range,
      count: inventory.filter(i => (i.reel_weight || 0) >= r.min && (i.reel_weight || 0) <= r.max).length
    }));
    setWeightDistributionData(weightDist);

    // 8. Stock by Dealer (Treemap data)
    const dealerCounts = {};
    inventory.filter(i => i.dealer_name).forEach(item => {
      dealerCounts[item.dealer_name] = (dealerCounts[item.dealer_name] || 0) + 1;
    });
    setDealerData(Object.entries(dealerCounts).map(([name, size], idx) => ({ 
      name, 
      size,
      fill: COLORS[idx % COLORS.length]
    })).sort((a, b) => b.size - a.size).slice(0, 10));
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  };

  const clearFilters = () => {
    setFilters({
      productType: "all",
      millName: "all",
      dealerName: "all",
      status: "all",
      dateFrom: "",
      dateTo: "",
    });
  };

  const hasActiveFilters = () => {
    return filters.productType !== "all" || 
           filters.millName !== "all" || 
           filters.dealerName !== "all" || 
           filters.status !== "all" ||
           filters.dateFrom !== "" ||
           filters.dateTo !== "";
  };

  const hasLowStock = stats.lowStockItems > 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-900 border border-cyan-500/50 p-3 rounded-lg shadow-lg">
          <p className="text-white font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color || entry.fill || '#10b981' }}>
              {entry.name || entry.dataKey}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
              {entry.dataKey === 'weight' || entry.dataKey === 'value' ? (entry.dataKey === 'weight' ? ' kg' : ' ₹') : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomTreemapContent = ({ x, y, width, height, name, size, fill }) => {
    if (width < 50 || height < 30) return null;
    return (
      <g>
        <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#1f2937" strokeWidth={2} rx={4} />
        <text x={x + width / 2} y={y + height / 2 - 8} textAnchor="middle" fill="#fff" fontSize={12} fontWeight="bold">
          {name?.length > 12 ? name.substring(0, 12) + '...' : name}
        </text>
        <text x={x + width / 2} y={y + height / 2 + 10} textAnchor="middle" fill="#9ca3af" fontSize={11}>
          {size} reels
        </text>
      </g>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-cyan-400 tracking-wider">
            Store Dashboard
          </h1>
          <p className="text-gray-400 mt-2">Inventory analytics and insights</p>
        </div>
        <Button
          onClick={handleRefresh}
          variant="outline"
          className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
          disabled={refreshing}
        >
          <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Filter Section */}
      <Card className="bg-gray-800/50 border border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-cyan-400 flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filter Dashboard Data
            {hasActiveFilters() && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-cyan-500/30 text-cyan-300 rounded-full">
                Filters Active
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <Label className="text-cyan-300 text-sm">Product Type</Label>
              <Select value={filters.productType} onValueChange={(v) => setFilters(prev => ({ ...prev, productType: v }))}>
                <SelectTrigger className="bg-gray-900 border-gray-600 text-white mt-1">
                  <SelectValue placeholder="All Products" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-600">
                  <SelectItem value="all">All Products</SelectItem>
                  {productTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-cyan-300 text-sm">Mill Name</Label>
              <Select value={filters.millName} onValueChange={(v) => setFilters(prev => ({ ...prev, millName: v }))}>
                <SelectTrigger className="bg-gray-900 border-gray-600 text-white mt-1">
                  <SelectValue placeholder="All Mills" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-600">
                  <SelectItem value="all">All Mills</SelectItem>
                  {millNames.map(mill => (
                    <SelectItem key={mill} value={mill}>{mill}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-cyan-300 text-sm">Dealer Name</Label>
              <Select value={filters.dealerName} onValueChange={(v) => setFilters(prev => ({ ...prev, dealerName: v }))}>
                <SelectTrigger className="bg-gray-900 border-gray-600 text-white mt-1">
                  <SelectValue placeholder="All Dealers" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-600">
                  <SelectItem value="all">All Dealers</SelectItem>
                  {dealerNames.map(dealer => (
                    <SelectItem key={dealer} value={dealer}>{dealer}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-cyan-300 text-sm">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="bg-gray-900 border-gray-600 text-white mt-1">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-600">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="stock_used">Stock Used</SelectItem>
                  <SelectItem value="return">Return</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-cyan-300 text-sm">Date From</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                className="bg-gray-900 border-gray-600 text-white mt-1 [color-scheme:dark]"
              />
            </div>
            <div>
              <Label className="text-cyan-300 text-sm">Date To</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                className="bg-gray-900 border-gray-600 text-white mt-1 [color-scheme:dark]"
              />
            </div>
          </div>
          {hasActiveFilters() && (
            <div className="mt-4 flex items-center gap-4">
              <Button
                onClick={clearFilters}
                variant="outline"
                size="sm"
                className="border-red-500/50 text-red-400 hover:bg-red-500/20"
              >
                <X className="h-4 w-4 mr-2" />
                Clear All Filters
              </Button>
              <span className="text-sm text-gray-400">
                Showing {filteredInventory.length} of {allInventory.length} items
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Stats Cards - Larger Value Display */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card 
          className="bg-gray-800/50 border border-green-500/30 cursor-pointer transition-all hover:scale-105"
          onClick={() => navigate("/store/inventory?status=available")}
          data-testid="available-stock-card"
        >
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center gap-2">
              <CheckCircle className="h-10 w-10 text-green-400" />
              <p className="text-4xl font-black text-green-400">{loading ? "..." : stats.availableStock}</p>
              <p className="text-sm font-medium text-green-300/70">Available Stock</p>
              <p className="text-xs text-green-400/50">Click to view</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={cn(
            "border-2 cursor-pointer transition-all hover:scale-105", 
            hasLowStock 
              ? "bg-gradient-to-br from-red-900/40 to-red-800/20 border-red-500/50 animate-pulse" 
              : "bg-gradient-to-br from-red-900/20 to-red-800/10 border-red-500/30"
          )}
          onClick={() => navigate("/store/low-stock")}
          data-testid="low-stock-card"
        >
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center gap-2">
              <AlertTriangle className={cn("h-10 w-10", hasLowStock ? "text-red-500" : "text-red-400")} />
              <p className={cn("text-4xl font-black", hasLowStock ? "text-red-500" : "text-red-400")}>{loading ? "..." : stats.lowStockItems}</p>
              <p className="text-sm font-medium text-red-300/70">Low Stock Alert</p>
              <p className="text-xs text-red-400/50">Click to view</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-br from-gray-800/40 to-gray-700/20 border-2 border-gray-500/30 cursor-pointer transition-all hover:scale-105"
          onClick={() => navigate("/store/inventory?status=stock_used")}
          data-testid="stock-used-card"
        >
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center gap-2">
              <Clock className="h-10 w-10 text-gray-400" />
              <p className="text-4xl font-black text-gray-400">{loading ? "..." : stats.usedStock}</p>
              <p className="text-sm font-medium text-gray-400/70">Stock Used</p>
              <p className="text-xs text-gray-500/50">Click to view</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-br from-amber-900/40 to-amber-800/20 border-2 border-amber-500/30 cursor-pointer transition-all hover:scale-105"
          onClick={() => navigate("/store/inventory?status=return")}
          data-testid="return-stock-card"
        >
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center gap-2">
              <RotateCcw className="h-10 w-10 text-amber-400" />
              <p className="text-4xl font-black text-amber-400">{loading ? "..." : stats.returnStock}</p>
              <p className="text-sm font-medium text-amber-300/70">Return</p>
              <p className="text-xs text-amber-400/50">Click to view</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-gray-800/40 to-gray-700/20 border-2 border-gray-600/30">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center gap-2">
              <Scale className="h-10 w-10 text-cyan-400" />
              <p className="text-4xl font-black text-cyan-400">{loading ? "..." : stats.totalWeight.toLocaleString()}</p>
              <p className="text-sm font-medium text-cyan-300/70">Total Weight (kg)</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-900/40 to-orange-800/20 border-2 border-orange-500/30">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center gap-2">
              <Activity className="h-10 w-10 text-orange-400" />
              <p className="text-4xl font-black text-orange-400">{loading ? "..." : stats.avgReelWeight}</p>
              <p className="text-sm font-medium text-orange-300/70">Avg Weight/Reel</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Total Value - Separate Large Card */}
      <Card className="bg-gradient-to-br from-cyan-900/60 to-cyan-800/40 border-4 border-cyan-500/50 shadow-2xl shadow-cyan-500/20">
        <CardContent className="p-8">
          <div className="flex flex-col items-center text-center gap-3">
            <TrendingUp className="h-16 w-16 text-cyan-400" />
            <p className="text-6xl font-black text-cyan-400 tracking-wider">
              ₹{loading ? "..." : stats.totalValue.toLocaleString()}
            </p>
            <p className="text-xl font-bold text-cyan-300/80">Total Inventory Value</p>
          </div>
        </CardContent>
      </Card>

      {/* Charts Row 1 - Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status Distribution - Donut */}
        <Card className="bg-black/60 border-2 border-cyan-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-cyan-400 flex items-center gap-2">
              <PieChartIcon className="h-4 w-4" />
              Stock Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statusPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={statusPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {statusPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="bottom" height={30} formatter={(value) => <span className="text-gray-300 text-xs">{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-500">No data</div>
            )}
          </CardContent>
        </Card>

        {/* Product Distribution - Donut */}
        <Card className="bg-black/60 border-2 border-blue-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-blue-400 flex items-center gap-2">
              <PieChartIcon className="h-4 w-4" />
              By Product Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {productTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={productTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {productTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="bottom" height={30} formatter={(value) => <span className="text-gray-300 text-xs">{value?.length > 15 ? value.substring(0,15)+'...' : value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-500">No data</div>
            )}
          </CardContent>
        </Card>

        {/* Available Stock by Source */}
        <Card className="bg-black/60 border-2 border-gray-600/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-cyan-400 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Available Stock by Source
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              // Calculate available stock by mill, dealer, and product
              const availableItems = (filteredInventory || []).filter(i => i.status === "Available" || i.status === "available");
              
              // Group by Mill
              const byMill = {};
              const byDealer = {};
              const byProduct = {};
              
              availableItems.forEach(item => {
                const mill = item.mill_name || "Unknown";
                const dealer = item.dealer_name || "Unknown";
                const product = item.product || "Unknown";
                
                byMill[mill] = (byMill[mill] || 0) + 1;
                byDealer[dealer] = (byDealer[dealer] || 0) + 1;
                byProduct[product] = (byProduct[product] || 0) + 1;
              });
              
              const millChartData = Object.entries(byMill)
                .map(([name, count]) => ({ name: name.substring(0, 15), count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);
              
              return availableItems.length > 0 ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-400 mb-2">Top 5 Mills with Available Stock</p>
                    <ResponsiveContainer width="100%" height={150}>
                      <BarChart data={millChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis type="number" stroke="#9ca3af" />
                        <YAxis type="category" dataKey="name" stroke="#9ca3af" width={90} tick={{ fontSize: 10 }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '8px' }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]}>
                          {millChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-cyan-500/10 p-2 rounded">
                      <p className="text-gray-400">Unique Mills</p>
                      <p className="text-cyan-400 font-bold text-lg">{Object.keys(byMill).length}</p>
                    </div>
                    <div className="bg-cyan-500/10 p-2 rounded">
                      <p className="text-gray-400">Unique Dealers</p>
                      <p className="text-cyan-400 font-bold text-lg">{Object.keys(byDealer).length}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-gray-500">No available stock</div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 - Mill Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stock by Mill - Horizontal Bar */}
        <Card className="bg-black/60 border-2 border-gray-600/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-cyan-400 flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Stock Count by Mill
            </CardTitle>
          </CardHeader>
          <CardContent>
            {millData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={millData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9ca3af" />
                  <YAxis type="category" dataKey="name" stroke="#9ca3af" width={70} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]}>
                    {millData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">No data</div>
            )}
          </CardContent>
        </Card>

        {/* Value by Mill - Composed */}
        <Card className="bg-black/60 border-2 border-cyan-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-cyan-400 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Value & Count by Mill (₹)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {valueByMillData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={valueByMillData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9ca3af" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                  <YAxis yAxisId="left" stroke="#06b6d4" />
                  <YAxis yAxisId="right" orientation="right" stroke="#f59e0b" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="value" fill="#06b6d4" name="Value (₹)" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="count" stroke="#f59e0b" name="Count" strokeWidth={2} dot={{ fill: '#f59e0b' }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">No data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 3 - Trends & Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weight by Product - Area */}
        <Card className="bg-black/60 border-2 border-cyan-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-cyan-400 flex items-center gap-2">
              <Scale className="h-4 w-4" />
              Total Weight by Product (kg)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {weightByProductData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={weightByProductData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9ca3af" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="weight" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">No data</div>
            )}
          </CardContent>
        </Card>

        {/* Stock by Dealer - Treemap */}
        <Card className="bg-black/60 border-2 border-gray-600/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-cyan-400 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Stock by Dealer
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dealerData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <Treemap
                  data={dealerData}
                  dataKey="size"
                  aspectRatio={4 / 3}
                  stroke="#1f2937"
                  content={<CustomTreemapContent />}
                >
                  <Tooltip content={<CustomTooltip />} />
                </Treemap>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">No data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stock Trend by Month */}
      {stockTrendData.length > 1 && (
        <Card className="bg-black/60 border-2 border-pink-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-pink-400 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Monthly Stock Movement Trend
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">Shows stock movement pattern over time by status</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={stockTrendData}>
                <defs>
                  <linearGradient id="colorAvailable" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorUsed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6b7280" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6b7280" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorReturn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                <YAxis stroke="#9ca3af" label={{ value: 'Stock Count', angle: -90, position: 'insideLeft', fill: '#9ca3af', fontSize: 11 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                  formatter={(value, name) => [`${value} items`, name]}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '10px' }}
                  formatter={(value) => <span className="text-gray-300 text-sm">{value}</span>}
                />
                <Area type="monotone" dataKey="available" stroke="#10b981" fill="url(#colorAvailable)" name="Available" strokeWidth={2} />
                <Area type="monotone" dataKey="used" stroke="#6b7280" fill="url(#colorUsed)" name="Used" strokeWidth={2} />
                <Area type="monotone" dataKey="return" stroke="#f59e0b" fill="url(#colorReturn)" name="Return" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-2 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                <span className="text-gray-400">Available - Ready to use</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                <span className="text-gray-400">Used - Consumed in production</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span className="text-gray-400">Return - Returned stock</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Low Stock Details */}
      {hasLowStock && (
        <Card className="bg-black/60 border-2 border-red-500/50 animate-pulse">
          <CardHeader>
            <CardTitle className="text-xl text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Low Stock Items - Reorder Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-red-500/30">
                    <th className="text-left py-2 text-red-300">Product Code</th>
                    <th className="text-left py-2 text-red-300">Product Type</th>
                    <th className="text-left py-2 text-red-300">Min Stock</th>
                    <th className="text-left py-2 text-red-300">Current Stock</th>
                    <th className="text-left py-2 text-red-300">Mill - Dealer</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockDetails.map((item, idx) => (
                    <tr key={idx} className="border-b border-red-500/20 hover:bg-red-500/10">
                      <td className="py-2 text-white font-mono">{item.product_code}</td>
                      <td className="py-2 text-gray-300">{item.product_type || "-"}</td>
                      <td className="py-2 text-cyan-400">{item.min_stock}</td>
                      <td className="py-2 text-red-400 font-bold">{item.current_stock}</td>
                      <td className="py-2 text-gray-300">
                        {item.mill_name && item.dealer_name 
                          ? `${item.mill_name} - ${item.dealer_name}`
                          : item.mill_name || item.dealer_name || "-"
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Consumption Inventory Section */}
      {consumptionItems.length > 0 && (
        <Card className="bg-black/60 border-2 border-emerald-500/30" data-testid="consumption-dashboard-section">
          <CardHeader>
            <CardTitle className="text-xl text-emerald-400 flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Consumption Inventory
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Stats Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-center">
                <p className="text-2xl font-black text-emerald-400">{consumptionItems.length}</p>
                <p className="text-xs text-emerald-300/70">Total Items</p>
              </div>
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-center">
                <p className="text-2xl font-black text-yellow-400">{consumptionItems.filter(i => i.available_qty <= 0 || (i.stock_count > 0 && (i.available_count || 0) <= 0)).length}</p>
                <p className="text-xs text-yellow-300/70">Out of Stock</p>
              </div>
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg text-center">
                <p className="text-2xl font-black text-cyan-400">
                  ₹{consumptionItems.reduce((sum, i) => sum + (i.available_qty * (i.unit_price || 0)), 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                </p>
                <p className="text-xs text-cyan-300/70">Stock Value</p>
              </div>
              <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg text-center">
                <p className="text-2xl font-black text-orange-400">{consumptionHistory.length}</p>
                <p className="text-xs text-orange-300/70">Stock-Outs (Recent)</p>
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* By Category Chart */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-bold text-emerald-300 mb-3">Stock by Category</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={(() => {
                    const catMap = {};
                    consumptionItems.forEach(i => {
                      const cat = i.category || "Other";
                      if (!catMap[cat]) catMap[cat] = { name: cat.length > 15 ? cat.substring(0, 15) + "..." : cat, available: 0, used: 0, fullName: cat };
                      catMap[cat].available += i.available_qty || 0;
                      catMap[cat].used += i.used_qty || 0;
                    });
                    return Object.values(catMap);
                  })()} layout="vertical" margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                    <YAxis dataKey="name" type="category" tick={{ fill: '#9ca3af', fontSize: 10 }} width={110} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="available" stackId="a" fill="#10b981" name="Available" />
                    <Bar dataKey="used" stackId="a" fill="#6b7280" name="Used" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Recent Stock Out Activity */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-bold text-emerald-300 mb-3">Recent Stock Out</h3>
                <div className="max-h-[200px] overflow-y-auto space-y-2">
                  {consumptionHistory.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-6">No stock-out records yet</p>
                  ) : consumptionHistory.slice(0, 10).map(log => (
                    <div key={log.id} className="flex justify-between items-center px-3 py-2 bg-gray-900/50 rounded-md">
                      <div>
                        <span className="text-white text-sm font-medium">{log.item_name}</span>
                        <span className="text-gray-500 text-xs ml-2">by {log.person_name}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-orange-400 font-bold text-sm">-{log.quantity} {log.unit}</span>
                        <p className="text-gray-600 text-xs">{new Date(log.timestamp).toLocaleDateString("en-IN")}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="py-2 text-emerald-400 text-xs font-bold">Category</th>
                    <th className="py-2 text-emerald-400 text-xs font-bold">Item</th>
                    <th className="py-2 text-emerald-400 text-xs font-bold text-center">Available</th>
                    <th className="py-2 text-emerald-400 text-xs font-bold text-center">Nos</th>
                    <th className="py-2 text-emerald-400 text-xs font-bold text-center">Used</th>
                    <th className="py-2 text-emerald-400 text-xs font-bold text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {consumptionItems.map(item => (
                    <tr key={item.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                      <td className="py-1.5 text-gray-400 text-xs">{item.category}</td>
                      <td className="py-1.5 text-white text-sm">{item.item_name}</td>
                      <td className={`py-1.5 text-center font-bold text-sm ${item.available_qty <= 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        {item.available_qty} {item.unit}
                      </td>
                      <td className="py-1.5 text-center text-sm">
                        {item.stock_count > 0 ? (
                          <span className={`font-bold ${(item.available_count || 0) <= 0 ? 'text-red-400' : 'text-cyan-400'}`}>
                            {item.available_count || 0}/{item.stock_count}
                          </span>
                        ) : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="py-1.5 text-center text-orange-400 text-sm">{item.used_qty || 0} {item.unit}</td>
                      <td className="py-1.5 text-right text-gray-300 text-sm">
                        {item.unit_price ? `₹${(item.available_qty * item.unit_price).toLocaleString(undefined, {maximumFractionDigits: 0})}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="bg-black/60 border-2 border-cyan-500/30">
        <CardHeader>
          <CardTitle className="text-xl text-cyan-400">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <button 
              onClick={() => navigate("/store/inventory")}
              className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/20 transition-all text-left group"
            >
              <Boxes className="h-8 w-8 text-cyan-400 mb-2 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-semibold text-white">View Inventory</h3>
              <p className="text-sm text-gray-400">Check current stock levels</p>
            </button>
            <button 
              onClick={() => navigate("/store/materials")}
              className="p-4 bg-cyan-500/10 border border-gray-600/30 rounded-lg hover:bg-cyan-500/20 transition-all text-left group"
            >
              <Package className="h-8 w-8 text-cyan-400 mb-2 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-semibold text-white">Manage Materials</h3>
              <p className="text-sm text-gray-400">Add or edit raw materials</p>
            </button>
            <button 
              onClick={() => navigate("/store/low-stock")}
              data-testid="low-stock-quick-action"
              className={cn(
                "p-4 border rounded-lg transition-all text-left group",
                hasLowStock 
                  ? "bg-red-500/20 border-red-500/50 hover:bg-red-500/30 animate-pulse" 
                  : "bg-red-500/10 border-red-500/30 hover:bg-red-500/20"
              )}
            >
              <AlertTriangle className={cn("h-8 w-8 mb-2 group-hover:scale-110 transition-transform", hasLowStock ? "text-red-500 animate-bounce" : "text-red-400")} />
              <h3 className="text-lg font-semibold text-white">Low Stock Report</h3>
              <p className={cn("text-sm", hasLowStock ? "text-red-400 font-medium" : "text-gray-400")}>
                {hasLowStock ? `${stats.lowStockItems} items to reorder!` : "View items to reorder"}
              </p>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StoreDashboard;
